import { fixWebmDuration } from '@fix-webm-duration/fix'
import { setLocalStorage, getLocalStorage, deleteLocalStorage } from '~/localStorage'
import { groupBy } from '~/utils/array'
import { classifySubjectId, extractSubjectId, SubjectId } from '~/utils/string'
import * as WebRecorder2PubSub from './pubSub'
import { assert } from '~/utils/assert'

declare global {
  interface Window {
    jump: {
      current_user: {
        id: string
      }
    }
  }
}

export type State = {
  db: IDBDatabase
  id: string
  writerId: string
  subjectId: string
  userId: string
}

type MeetingOrIntRefId = string

const dbNamePrefix = 'jump-'
const idPrefix = 'record-ui-'
const userId = window.jump?.current_user?.id ?? 'not-signed-in'

const localStorageKeys = ['isRecording', 'recordedTime', 'startTime', 'mimeType', 'recordingSubmitted']

export async function createRecordingStorage(subjectId: MeetingOrIntRefId, writerId: string): Promise<State> {
  const state = await openAndMigrateDb(subjectId, userId, writerId, { createIfMissing: true })

  // Tyler notes: there's a race condition between initializing the sidebar
  // (where we subscribe to 'is-recordable' via `useUnsubmittedRecordings`) and
  // the recorder singleton initializing itself. Irritatingly, repeatedly publishing
  // is the best solution I've come up with to ensure the sidebar gets the message.
  for (const timeout of [0, 500, 1000]) {
    setTimeout(() => WebRecorder2PubSub.publish('is-recordable', { subjectId }), timeout)
  }

  return state
}

export async function openExistingStorage(subjectId: MeetingOrIntRefId, writerId: string): Promise<State | undefined> {
  try {
    return await openAndMigrateDb(subjectId, userId, writerId, { createIfMissing: false })
  } catch (_) {
    return undefined
  }
}

async function openAndMigrateDb(
  subjectId: MeetingOrIntRefId,
  userId: string,
  writerId: string,
  { createIfMissing }: { createIfMissing?: boolean } = {}
): Promise<State> {
  const version = 1

  const oldFormatDbName = formatDatabaseName(subjectId, undefined)
  const newFormatDbName = formatDatabaseName(subjectId, userId)

  const knownDatabases = await indexedDB.databases()
  const oldFormatDbExisted = knownDatabases.some(({ name }) => name === oldFormatDbName)
  const newFormatDbExisted = knownDatabases.some(({ name }) => name === newFormatDbName)

  const oldId = formatDatabaseId(subjectId, undefined)
  const newId = formatDatabaseId(subjectId, userId)

  const newDb =
    createIfMissing || newFormatDbExisted || oldFormatDbExisted
      ? await openOrCreateDb(newFormatDbName, version, { createIfMissing: true })
      : undefined

  // TODO: A few weeks after #2396 is deployed, remove the migration code
  if (oldFormatDbExisted && !newFormatDbExisted) {
    const oldDb = await openOrCreateDb(oldFormatDbName, version, { createIfMissing: false })
    assert(newDb !== undefined, 'newDb should have been created above')

    const oldState = { db: oldDb, id: oldId, writerId, subjectId, userId: '' }
    const newState = { db: newDb, id: newId, writerId, subjectId, userId }

    try {
      await migrateDb(oldState, newState)
    } catch (error) {
      debugLog(`Error migrating recording of ${subjectId} to new format`, error)

      // Delete the *new* database so that we'll try the migration again next time
      newState.db.close()
      indexedDB.deleteDatabase(newFormatDbName)

      throw error
    }
  }

  if (newDb) {
    return { db: newDb, id: newId, writerId, subjectId, userId }
  } else {
    throw new Error('No capture database found')
  }
}

async function migrateDb(fromState: State, toState: State) {
  debugLog(`Migrating existing recording of ${fromState.subjectId} to new format`)

  for (const chunk of await getAllChunks(fromState)) {
    await addChunk(toState, chunk)
  }

  for (const key of localStorageKeys) {
    const value = getLocalStorage(fromState.id, key, null)

    if (value !== null) {
      setLocalStorage(toState.id, key, value)
    }

    // Ensure we won't use old local storage keys to try to show the recording in the Live Notetaker
    deleteLocalStorage(fromState.id, key)
  }

  // Delete old DB
  const oldDbName = fromState.db.name
  fromState.db.close()
  await deleteDb(oldDbName)
  assert(!(await indexedDB.databases()).some(({ name }) => name === oldDbName), 'oldDb should have been deleted')
}

async function deleteDb(dbName: string) {
  const request = indexedDB.deleteDatabase(dbName)

  return new Promise(async (resolve, reject) => {
    request.onerror = reject
    request.onsuccess = resolve
  })
}

function openOrCreateDb(
  dbName: string,
  version: number,
  { createIfMissing }: { createIfMissing?: boolean } = {}
): Promise<IDBDatabase> {
  const dbTable = 'audioChunks'

  return new Promise(async (resolve, reject) => {
    // Try opening existing database with new format
    const request = indexedDB.open(dbName, version)

    request.onerror = reject
    request.onblocked = () => reject(new Error('IndexedDB blocked'))

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result
      if (!db.objectStoreNames.contains(dbTable) && createIfMissing) {
        db.createObjectStore(dbTable, { autoIncrement: true })
      } else {
        reject(new Error('Upgrade needed'))
      }
    }

    request.onsuccess = (event) => {
      const db = (event.target as IDBOpenDBRequest).result
      resolve(db)
    }
  })
}

export function addChunk(storage: State, chunk: Blob): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = storage.db.transaction(['audioChunks'], 'readwrite')
    const store = transaction.objectStore('audioChunks')
    try {
      const request = store.add(chunk)

      request.onsuccess = () => resolve()
      request.onerror = reject
    } catch (error) {
      // Tyler says: store.add() can throw on some versions of Safari that don't
      // support blobs being passed to the add() method. This is very bad,
      // and we need to surface a user-visible error in this case.
      reject(error)
    }
  })
}

export function getAllChunks(storage: State): Promise<Blob[]> {
  return new Promise((resolve, reject) => {
    const transaction = storage.db.transaction(['audioChunks'], 'readonly')
    const store = transaction.objectStore('audioChunks')
    const request = store.getAll()

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(new Error('Failed to get chunks'))
  })
}

export function clear(storage: State): Promise<void> {
  return new Promise(async (resolve, reject) => {
    const transaction = storage.db.transaction(['audioChunks'], 'readwrite')
    const store = transaction.objectStore('audioChunks')
    const request = store.clear()

    setIsRecording(storage, false)
    clearStartTime(storage)
    setDurationMs(storage, 0)

    // Also clear leftover local storage keys from pre-migration
    const oldId = formatDatabaseId(storage.subjectId, undefined)

    for (const key of localStorageKeys) {
      deleteLocalStorage(oldId, key)
    }

    request.onsuccess = () => {
      WebRecorder2PubSub.publish('recording-cleared', { subjectId: storage.subjectId })
      resolve()
    }
    request.onerror = () => reject(new Error('Failed to clear storage'))
  })
}

// Local storage methods
export function setIsRecording(storage: State, value: boolean): void {
  setLocalStorage(storage.id, 'isRecording', value)
}

export function getIsRecording(storage: State): boolean {
  return getLocalStorage(storage.id, 'isRecording', false)
}

export function setDurationMs(storage: State, ms: number): void {
  setLocalStorage(storage.id, 'recordedTime', ms)
}

export function getDurationMs(storage: State): number {
  return getLocalStorageNumber(storage, 'recordedTime', 0)
}

export function clearStartTime(storage: State): void {
  setLocalStorage(storage.id, 'startTime', null)
}

export function getStartTimeMs(storage: State): number {
  return getLocalStorageNumber(storage, 'startTime', 0)
}

export function setStartTimeMs(storage: State, ms: number): void {
  setLocalStorage(storage.id, 'startTime', ms)
}

export function setMimeType(storage: State, type: string): void {
  setLocalStorage(storage.id, 'mimeType', type)
}

export function getMimeType(storage: State): string {
  return getLocalStorage(storage.id, 'mimeType', '')
}

export function setRecordingSubmitted(storage: State): void {
  setLocalStorage(storage.id, 'recordingSubmitted', true)
}

export function getRecordingSubmitted(storage: State): boolean {
  return getLocalStorage(storage.id, 'recordingSubmitted', false)
}

function getLocalStorageNumber(storage: State, key: string, defaultValue: number): number {
  const value = getLocalStorage(storage.id, key, defaultValue)
  return value && !isNaN(value) ? value : defaultValue
}

export async function downloadableBlob(storage: State): Promise<Blob> {
  const mimeType = getMimeType(storage)
  const audioChunks = await getAllChunks(storage)
  const durationMs = getDurationMs(storage)
  const baseMimeType = mimeType.split(';')[0]

  return baseMimeType === 'audio/webm'
    ? fixWebmDuration(new Blob(audioChunks, { type: mimeType }), durationMs)
    : new Blob(audioChunks, { type: mimeType })
}

export async function clearCompletedRecordingStorage({
  meetingId,
  integrationReferenceId,
  writerId,
}: {
  meetingId: string
  integrationReferenceId: string
  writerId: string
}): Promise<void> {
  const possibleIds = [meetingId, integrationReferenceId].filter((id) => typeof id === 'string' && id.length > 0)

  for (const id of possibleIds) {
    for (const possibleStorageKey of [formatDatabaseId(id, userId), formatDatabaseId(id, undefined)]) {
      try {
        const storage = await openExistingStorage(possibleStorageKey, writerId)

        if (storage) {
          await clear(storage)
          debugLog(`Cleared local recording storage for ${possibleStorageKey}`)
        }
      } catch (_error) {
        // Ignore errors
      }
    }
  }
}

export type RecordingDatabaseMetadata = SubjectId & {
  name?: string
  submitted: boolean
  durationMs: number
  totalBytes?: number
  startedAtUnixMs?: number
  recordingInProgress: boolean
}

declare global {
  interface Window {
    debugListLocalRecordings: () => Promise<RecordingDatabaseMetadata[]>
    listLocalRecordings: () => RecordingDatabaseMetadata[]
  }
}

export function listLocalRecordings(): RecordingDatabaseMetadata[] {
  const recordingKVPairs = Object.entries(localStorage)
    .filter(([key, _val]) => key.startsWith(idPrefix))
    .filter(([key, _val]) => !!extractSubjectId(key))

  const recordingKVPairsBySubjectId = groupBy(recordingKVPairs, ([key, _val]) => {
    const subjectId = extractSubjectId(key)!
    const userId = extractUserIdFromDatabaseName(key)
    return userId ? `${subjectId}-user-${userId}` : subjectId
  })

  return Object.entries(recordingKVPairsBySubjectId)
    .map(([key, kvPairs]) => {
      const submittedPair = kvPairs.find(([key, _val]) => key.includes('recordingSubmitted'))
      const durationMsPair = kvPairs.find(([key, _val]) => key.includes('recordedTime'))
      const startTimePair = kvPairs.find(([key, _val]) => key.includes('startTime'))
      const recordingInProgressPair = kvPairs.find(([key, _val]) => key.includes('isRecording'))
      const [subjectId] = key.split('-user-') // Split off user ID if present

      const startedTime = startTimePair ? parseInt(startTimePair[1]) : undefined

      return {
        ...classifySubjectId(subjectId),
        submitted: submittedPair ? submittedPair[1] === 'true' : false,
        durationMs: durationMsPair ? parseInt(durationMsPair[1]) : 0,
        startedAtUnixMs: startedTime && !isNaN(startedTime) ? startedTime : undefined,
        recordingInProgress: recordingInProgressPair ? recordingInProgressPair[1] === 'true' : false,
      }
    })
    .filter((x) => x.durationMs > 0)
}

export function mostRecentUnsubmittedRecording({
  withinLastMs,
  now,
}: {
  withinLastMs?: number
  now?: number
} = {}): RecordingDatabaseMetadata | undefined {
  const nowMs = now ?? Date.now()

  const meetsCutoff =
    typeof withinLastMs === 'number'
      ? (r: RecordingDatabaseMetadata) => {
          const cutoff = nowMs - withinLastMs
          return r.startedAtUnixMs && r.startedAtUnixMs >= cutoff
        }
      : (_: RecordingDatabaseMetadata) => true

  return listLocalRecordings()
    .filter((r) => !r.submitted && typeof r.startedAtUnixMs === 'number' && meetsCutoff(r))
    .sort((a, b) => b.startedAtUnixMs! - a.startedAtUnixMs!)
    .at(0)
}

window.listLocalRecordings = listLocalRecordings

window.debugListLocalRecordings = async () => {
  const databases = await indexedDB.databases()

  const recordingDatabaseMetadata: RecordingDatabaseMetadata[] = (
    await Promise.all(
      databases
        .filter(({ name }) => name && name.startsWith(dbNamePrefix))
        .map(async ({ name, version }) => {
          if (!name || !version) return null

          const dbId = name.replace(dbNamePrefix, '')
          const userId = extractUserIdFromDatabaseName(dbId)
          const subjectId = extractSubjectId(dbId.replace(`-${userId}`, ''))
          if (!subjectId) return null

          const state = await openExistingStorage(subjectId, 'debug-not-a-writer')
          if (!state) return null

          const audioChunks = await getAllChunks(state)
          const totalBytes = audioChunks.reduce((acc, chunk) => acc + chunk.size, 0)
          const submitted = getRecordingSubmitted(state)
          const durationMs = getDurationMs(state)
          const startedAtUnixMs = getStartTimeMs(state)
          const recordingInProgress = getIsRecording(state)

          return {
            ...classifySubjectId(subjectId),
            name,
            totalBytes,
            submitted,
            durationMs,
            startedAtUnixMs,
            recordingInProgress,
          }
        })
    )
  )
    .filter((x) => !!x)
    .filter(isRecordingDatabaseMetadata)
    .filter((x) => x.submitted || x.totalBytes > 0 || x.durationMs > 0)

  const submitted = recordingDatabaseMetadata.filter((x) => x.submitted)
  const notSubmitted = recordingDatabaseMetadata.filter((x) => !x.submitted)

  /* eslint-disable-next-line no-console */
  console.log('✅ Previously submitted local meetings:')

  for (const submittedMetadata of submitted) {
    printRecordingDatabaseMetadata(submittedMetadata)
  }

  /* eslint-disable-next-line no-console */
  console.log('⚠️ Unsubmitted local meetings:')
  for (const notSubmittedMetadata of notSubmitted) {
    printRecordingDatabaseMetadata(notSubmittedMetadata)
  }

  return recordingDatabaseMetadata
}

function isRecordingDatabaseMetadata(x: any): x is RecordingDatabaseMetadata {
  return !!x && typeof x === 'object' && 'name' in x && 'totalBytes' in x && 'submitted' in x && 'durationMs' in x
}

function printRecordingDatabaseMetadata({ name, submitted, totalBytes, durationMs }: RecordingDatabaseMetadata) {
  const baseUrl = window.location.origin

  const dbId = name ? name.replace(dbNamePrefix, '') : ''
  const [idWithoutUser] = dbId.split('-usr_') // Remove user ID suffix if present
  const maybeId = idWithoutUser.replace(idPrefix, '')

  const urlOrLabel = maybeId.startsWith('mtg_')
    ? `${baseUrl}/meetings/${maybeId}`
    : maybeId.startsWith('int_ref_')
      ? `${baseUrl}/prepare/${maybeId}`
      : name

  if (submitted) {
    /* eslint-disable-next-line no-console */
    console.log(urlOrLabel)
  } else {
    /* eslint-disable-next-line no-console */
    console.log(`${urlOrLabel} (${durationMs / 1000} seconds, ${totalBytes} bytes)`)
  }
}

function formatDatabaseId(subjectId: string, userId: string | undefined): string {
  return userId ? `${idPrefix}${subjectId}-${userId}` : `${idPrefix}${subjectId}`
}

function formatDatabaseName(subjectId: string, userId: string | undefined): string {
  return `${dbNamePrefix}${formatDatabaseId(subjectId, userId)}`
}

function extractUserIdFromDatabaseName(name: string): string | undefined {
  // Our user IDs are formatted as usr_abc123
  const match = name.match(/-(usr_[^-^_]+)/)
  return match ? match[1] : undefined
}

function debugLog(...args: any[]) {
  /* eslint-disable-next-line no-console */
  console.log('[Recording Storage]', ...args)
}
